import React, { FC, useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  HiOutlineMenuAlt2, 
  HiOutlineBell, 
  HiOutlineSearch,
  HiOutlineMoon,
  HiOutlineSun,
  HiOutlineUser,
  HiOutlineCog,
  HiOutlineLogout
} from 'react-icons/hi';
import { useDashboard } from '../../../context';

import { useTheme } from '../../../context/ThemeContext';

interface ModernHeaderProps {
  user: any;
}

const ModernHeader: FC<ModernHeaderProps> = ({ user }) => {
  const { setSidebarOpen, sidebarCollapsed } = useDashboard();
  const { actualTheme, toggleTheme } = useTheme();
  const [showSearch, setShowSearch] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const notificationRef = useRef<HTMLDivElement>(null);
  
  const isDarkMode = actualTheme === 'dark';

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        setShowNotifications(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getUserInitials = (name: string) => {
    if (!name) return 'U';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  return (
    <header
      id='header'
      className={`
        fixed top-0 right-0 z-40 h-16 lg:h-[72px]
        bg-white/80 dark:bg-gray-900/80 
        backdrop-blur-lg
        border-b border-gray-200/50 dark:border-gray-800/50
        transition-all duration-300 ease-out
        ${sidebarCollapsed ? 'lg:left-20' : 'lg:left-72'}
        left-0
      `}
    >
      <div className='h-full px-4 lg:px-6 flex items-center justify-between gap-4'>
        {/* Left section */}
        <div className='flex items-center gap-4'>
          {/* Mobile menu button */}
          <button
            type='button'
            onClick={() => setSidebarOpen(true)}
            className='lg:hidden p-2 rounded-xl text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors'
          >
            <HiOutlineMenuAlt2 className='w-6 h-6' />
          </button>

          {/* Mobile logo */}
          <img 
            src='/assets/Dav-safaris-transparent-logo.png' 
            alt='Dav Safaris Logo' 
            className='h-10 w-auto lg:hidden'
          />

          {/* Search bar (desktop) */}
          <div className='hidden md:flex items-center'>
            <div className='relative'>
              <HiOutlineSearch className='absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400' />
              <input
                type='text'
                placeholder='Search...'
                className='
                  w-64 lg:w-80 h-10 pl-10 pr-4
                  bg-gray-100 dark:bg-gray-800
                  border-none rounded-xl
                  text-sm text-gray-900 dark:text-white
                  placeholder:text-gray-400
                  focus:outline-none focus:ring-2 focus:ring-primary-500/20
                  transition-all duration-200
                '
              />
              <kbd className='absolute right-3 top-1/2 -translate-y-1/2 hidden lg:inline-flex items-center gap-1 px-2 py-0.5 bg-white dark:bg-gray-700 rounded text-xs text-gray-400 border border-gray-200 dark:border-gray-600'>
                ⌘K
              </kbd>
            </div>
          </div>
        </div>

        {/* Right section */}
        <div className='flex items-center gap-2'>
          {/* Mobile search button */}
          <button
            type='button'
            onClick={() => setShowSearch(!showSearch)}
            className='md:hidden p-2 rounded-xl text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors'
          >
            <HiOutlineSearch className='w-5 h-5' />
          </button>

          {/* Theme toggle */}
          <button
            type='button'
            onClick={toggleTheme}
            className='p-2 rounded-xl text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors'
            title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {isDarkMode ? (
              <HiOutlineSun className='w-5 h-5' />
            ) : (
              <HiOutlineMoon className='w-5 h-5' />
            )}
          </button>

          {/* Notifications */}
          <div className='relative' ref={notificationRef}>
            <button
              type='button'
              onClick={() => setShowNotifications(!showNotifications)}
              className='relative p-2 rounded-xl text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors'
            >
              <HiOutlineBell className='w-5 h-5' />
              {/* Notification badge */}
              <span className='absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full' />
            </button>

            {/* Notifications dropdown */}
            {showNotifications && (
              <div className='absolute right-0 mt-2 w-80 bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 overflow-hidden animate-fade-in'>
                <div className='p-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between'>
                  <h3 className='font-semibold text-gray-900 dark:text-white'>Notifications</h3>
                  <button type='button' className='text-sm text-primary-500 hover:text-primary-600'>
                    Mark all read
                  </button>
                </div>
                <div className='max-h-80 overflow-y-auto'>
                  {/* Sample notifications */}
                  {[1, 2, 3].map((i) => (
                    <div 
                      key={i} 
                      className='p-4 border-b border-gray-50 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors'
                    >
                      <div className='flex gap-3'>
                        <div className='w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-500/20 flex items-center justify-center text-primary-600 dark:text-primary-400 flex-shrink-0'>
                          <HiOutlineBell className='w-5 h-5' />
                        </div>
                        <div className='flex-1 min-w-0'>
                          <p className='text-sm text-gray-900 dark:text-white'>
                            New booking received
                          </p>
                          <p className='text-xs text-gray-500 mt-1'>2 minutes ago</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className='p-3 bg-gray-50 dark:bg-gray-800/50 text-center'>
                  <Link 
                    to='/dashboard/notifications'
                    className='text-sm text-primary-500 hover:text-primary-600 font-medium'
                  >
                    View all notifications
                  </Link>
                </div>
              </div>
            )}
          </div>

          {/* User menu */}
          <div className='relative' ref={userMenuRef}>
            <button
              type='button'
              onClick={() => setShowUserMenu(!showUserMenu)}
              className='flex items-center gap-3 p-1.5 pr-3 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors'
            >
              <div className='w-9 h-9 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white font-semibold text-sm'>
                {getUserInitials(user?.username)}
              </div>
              <div className='hidden lg:block text-left'>
                <p className='text-sm font-medium text-gray-900 dark:text-white'>
                  {user?.username || 'User'}
                </p>
                <p className='text-xs text-gray-500 dark:text-gray-400 capitalize'>
                  {user?.role?.replace('_', ' ') || 'Member'}
                </p>
              </div>
            </button>

            {/* User dropdown */}
            {showUserMenu && (
              <div className='absolute right-0 mt-2 w-56 bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 overflow-hidden animate-fade-in'>
                <div className='p-4 border-b border-gray-100 dark:border-gray-800'>
                  <p className='font-semibold text-gray-900 dark:text-white'>
                    {user?.username || 'User'}
                  </p>
                  <p className='text-sm text-gray-500'>{user?.email}</p>
                </div>
                <div className='p-2'>
                  <Link
                    to='/dashboard/profile'
                    className='flex items-center gap-3 px-3 py-2 rounded-xl text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors'
                    onClick={() => setShowUserMenu(false)}
                  >
                    <HiOutlineUser className='w-5 h-5' />
                    <span className='text-sm'>Profile</span>
                  </Link>
                  <Link
                    to='/dashboard/settings'
                    className='flex items-center gap-3 px-3 py-2 rounded-xl text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors'
                    onClick={() => setShowUserMenu(false)}
                  >
                    <HiOutlineCog className='w-5 h-5' />
                    <span className='text-sm'>Settings</span>
                  </Link>
                </div>
                <div className='p-2 border-t border-gray-100 dark:border-gray-800'>
                  <button
                    type='button'
                    className='w-full flex items-center gap-3 px-3 py-2 rounded-xl text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors'
                  >
                    <HiOutlineLogout className='w-5 h-5' />
                    <span className='text-sm'>Log out</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile search overlay */}
      {showSearch && (
        <div className='md:hidden absolute top-full left-0 right-0 p-4 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 animate-fade-in'>
          <div className='relative'>
            <HiOutlineSearch className='absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400' />
            <input
              type='text'
              placeholder='Search...'
              className='
                w-full h-10 pl-10 pr-4
                bg-gray-100 dark:bg-gray-800
                border-none rounded-xl
                text-sm text-gray-900 dark:text-white
                placeholder:text-gray-400
                focus:outline-none focus:ring-2 focus:ring-primary-500/20
              '
            />
          </div>
        </div>
      )}
    </header>
  );
};

export default ModernHeader;
